'use client';
import { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { useGameOutcomes } from '@/hooks/sport/useGameOutcomes';
import { useLineSelection } from '@/hooks/sport/useLineSelection';
import { useRealtimeMarketPrices } from '@/hooks/sport/useRealtimeMarketPrices';
import { EventMarket, EventResponse } from '@/types';
import {
  findMarket,
  findOutcomeIndexForTeam,
  formatSportPrice,
  formatVolume,
  getMarketType,
  getOutcomes,
} from '@/utils';
import { MarketButton } from '../SportsGameCard/MarketButton';
import { LineSelector } from '../SportsGameCard/subcomponents/LineSelector';

interface GameDetailCardProps {
  event: EventResponse;
  marketType: string;
  title: string;
  activeOutcomeId?: string;
  selectedMarketType?: string | null;
  onTradeClick?: (
    marketId: string,
    outcomeId: string,
    marketType: string,
    isBuy: boolean,
    shouldOpenModal?: boolean,
  ) => void;
  onLineChange?: (line: string | number, marketType: string, marketId?: string) => void;
  market?: any;
  isPreviousMarket?: boolean;
  lineGroupMarkets?: EventMarket[];
  hasLineSlider?: boolean;
  is3Way?: boolean;
}

export const GameDetailCard = ({
  event,
  marketType,
  title,
  activeOutcomeId,
  onTradeClick,
  onLineChange,
  market: initialMarket,
  isPreviousMarket = false,
  lineGroupMarkets,
  hasLineSlider,
  is3Way = false,
}: GameDetailCardProps) => {
  const { t } = useT();
  const mtLower = marketType.toLowerCase();
  const isML = mtLower === 'moneyline';
  const isSpread =
    mtLower === 'spread' ||
    mtLower === 'spreads' ||
    mtLower.endsWith('_spreads') ||
    mtLower === 'map_handicap' ||
    mtLower === 'match_handicap' ||
    mtLower === 'tennis_set_handicap';
  const isTotal = mtLower === 'total' || mtLower === 'totals' || mtLower.endsWith('_totals');
  const isTeamLabel =
    isML || isSpread || mtLower === 'child_moneyline' || mtLower.endsWith('_moneyline');

  const spreadRailSelectsByMarketId = useMemo(
    () => Boolean(isSpread && lineGroupMarkets && lineGroupMarkets.length > 1),
    [isSpread, lineGroupMarkets],
  );

  const lineDisplayLabelsForRail = useMemo(() => {
    if (!spreadRailSelectsByMarketId || !lineGroupMarkets) return undefined;
    return lineGroupMarkets.map((m) => {
      const v = (m as any).line || (m as any).groupItemThreshold;
      const n = Number(v);
      return Number.isFinite(n) ? Math.abs(n) : (v ?? '');
    });
  }, [spreadRailSelectsByMarketId, lineGroupMarkets]);

  const { allLines, selectedLine, activeMarket, showLineRail, setUserSelectedLine } =
    useLineSelection({
      event,
      marketType,
      initialMarket,
      lineGroupMarkets,
      isML,
      isSpread,
      isTotal,
      spreadRailSelectsByMarketId,
      hasLineSlider,
    });

  const getMarketForTeam = (teamName?: string) => {
    if (!teamName) return undefined;
    const lowerName = teamName.toLowerCase().trim();
    return event.markets?.find((m) => {
      const type = getMarketType(m);
      const title = ((m as any).groupItemTitle || '').toLowerCase();
      return (
        type.includes('moneyline') &&
        title.includes(lowerName) &&
        !title.includes('draw') &&
        !title.includes('empate')
      );
    });
  };

  const homeMarket = isML
    ? getMarketForTeam(event.homeTeam?.name) || findMarket(event, 'moneyline')
    : activeMarket;
  let awayMarket = isML ? getMarketForTeam(event.awayTeam?.name) : null;

  // If both teams matched the same market, treat it as a shared market
  if (awayMarket && homeMarket && String(awayMarket.id) === String(homeMarket.id)) {
    awayMarket = null;
  }

  const drawMarket = isML ? findMarket(event, 'draw') : null;

  const { realtimePrices, getPriceUpdate } = useRealtimeMarketPrices(
    activeMarket,
    homeMarket,
    awayMarket,
    drawMarket,
  );

  const btnData = useGameOutcomes({
    event,
    activeMarket,
    isML,
    isTeamLabel,
    isSpread,
    isTotal,
    homeMarket,
    awayMarket,
    drawMarket,
    activeOutcomeId,
    realtimePrices,
    formatSportPrice,
  });

  const threeWayButtons = useMemo(() => {
    if (isML) return null;
    if (!is3Way || !lineGroupMarkets || lineGroupMarkets.length < 2) return null;

    return lineGroupMarkets.map((m) => {
      const outcomes = getOutcomes(m);
      const yesOutcome = outcomes.find((o) => o.name?.toLowerCase() === 'yes') || outcomes[0];
      const clobIds = m.clobTokenIds
        ? typeof m.clobTokenIds === 'string'
          ? JSON.parse(m.clobTokenIds)
          : m.clobTokenIds
        : [];
      const tokenId = clobIds[0];
      const pStatic = yesOutcome?.bestAsk || yesOutcome?.price;
      const p =
        realtimePrices && tokenId ? (realtimePrices[tokenId]?.askPrice ?? pStatic) : pStatic;

      const label = (m as any).groupItemTitle || yesOutcome?.name || '?';
      const isActive = !!activeOutcomeId && yesOutcome?.id === activeOutcomeId;

      return {
        label: label.toUpperCase(),
        price: formatSportPrice(p),
        id: yesOutcome?.id,
        marketId: m.id,
        active: isActive,
        color: label.toLowerCase() === 'draw' ? '#55504a' : undefined,
      };
    });
  }, [is3Way, lineGroupMarkets, realtimePrices, activeOutcomeId, isML]);

  const displayedCardVolume = useMemo(() => {
    if (lineGroupMarkets && lineGroupMarkets.length > 0) {
      return lineGroupMarkets.reduce(
        (sum, m) => sum + (parseFloat(m.volume as string) || (m as any).volumeNum || 0),
        0,
      );
    }
    if (!activeMarket) return 0;
    return parseFloat(String((activeMarket as any).volumeNum ?? activeMarket.volume ?? 0)) || 0;
  }, [lineGroupMarkets, activeMarket]);

  const activeVolume = `${formatVolume(displayedCardVolume)} Vol.`;

  const hideOutcomeButtons = useMemo(() => {
    if (!activeMarket) return false;
    const isClosed =
      (activeMarket as any).closed === true || (activeMarket as any).closed === 'true';
    if (!isClosed) return false;

    const bid = Number((activeMarket as any).bestBid);
    const ask = Number((activeMarket as any).bestAsk);
    const hasBid = Number.isFinite(bid) && bid > 0;
    const tinyAsk = Number.isFinite(ask) && ask <= 0.001;

    const outcomes = getOutcomes(activeMarket);
    if (outcomes.length !== 2) return false;
    const prices = outcomes.map((o) => Number(o.price));
    const hasBinaryTerminalPrices =
      prices.length === 2 &&
      prices.every((p) => Number.isFinite(p)) &&
      ((prices[0] <= 0.001 && prices[1] >= 0.999) || (prices[1] <= 0.001 && prices[0] >= 0.999));

    return !hasBid && tinyAsk && hasBinaryTerminalPrices;
  }, [activeMarket]);

  const handleOutcomeClick = (outcomeId: string, isBuy: boolean, overrideMarketId?: string) => {
    const mId = overrideMarketId || activeMarket?.id;
    if (!mId || !onTradeClick) return;
    onTradeClick(mId, outcomeId, marketType, isBuy);
  };

  if (!btnData) return null;
  const hasDrawButton = Boolean((btnData as any).draw);
  const compactThreeWaySx = hasDrawButton
    ? {
        minWidth: { xs: 84, sm: 96, md: 108 },
        width: { xs: '100%', sm: 108, md: 124 },
        flex: 1,
      }
    : undefined;

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 'none',
        mb: 2,
        boxShadow: 'none',
        overflow: 'hidden',
        color: 'text.primary',
      }}
    >
      <CardContent sx={{ p: { xs: '16px !important', sm: '20px !important' } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            gap: { xs: 1.5, sm: 2 },
            mb: showLineRail ? 2 : 0,
          }}
        >
          <Box sx={{ flex: { xs: '1 1 auto', sm: 1 }, minWidth: 0, pt: 0.5 }}>
            {title && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {mtLower === 'child_moneyline' && activeMarket?.groupItemTitle
                  ? String(activeMarket.groupItemTitle)
                  : t(`sports.${title.toLowerCase().replace(/ /g, '_')}`, { defaultValue: title })}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.8125rem',
              }}
            >
              {activeVolume}
            </Typography>
          </Box>

          {!isPreviousMarket && !hideOutcomeButtons && threeWayButtons && (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, sm: 1.5 },
                flexShrink: 0,
                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {threeWayButtons.map((btn) => (
                <MarketButton
                  key={btn.marketId}
                  label={btn.label}
                  price={btn.price}
                  active={btn.active}
                  color={btn.color}
                  onClick={() => handleOutcomeClick(btn.id, true, btn.marketId)}
                  sx={{
                    width: { xs: '100%', sm: 120, md: 150 },
                  }}
                />
              ))}
            </Box>
          )}
          {!isPreviousMarket && !hideOutcomeButtons && !threeWayButtons && (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.5, sm: 1.5 },
                flexShrink: 0,
                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <MarketButton
                label={(btnData as any).team1.label}
                line={(btnData as any).team1.line}
                price={(btnData as any).team1.price}
                active={(btnData as any).team1.active}
                color={(btnData as any).team1.color}
                onClick={() =>
                  handleOutcomeClick(
                    (btnData as any).team1.id,
                    true,
                    (btnData as any).team1.marketId,
                  )
                }
                priceChangeDirection={getPriceUpdate(0)?.direction}
                lastUpdateTimestamp={getPriceUpdate(0)?.timestamp}
                sx={{
                  width: { xs: '100%', sm: 120, md: 150 },
                  ...(compactThreeWaySx || {}),
                }}
              />
              {(btnData as any).draw && (
                <MarketButton
                  label={(btnData as any).draw.label}
                  price={(btnData as any).draw.price}
                  active={(btnData as any).draw.active}
                  color={(btnData as any).draw.color}
                  onClick={() =>
                    handleOutcomeClick(
                      (btnData as any).draw.id,
                      true,
                      (btnData as any).draw.marketId,
                    )
                  }
                  sx={{
                    width: { xs: '100%', sm: 108, md: 124 },
                  }}
                />
              )}
              <MarketButton
                label={(btnData as any).team2.label}
                line={(btnData as any).team2.line}
                price={(btnData as any).team2.price}
                active={(btnData as any).team2.active}
                color={(btnData as any).team2.color}
                onClick={() =>
                  handleOutcomeClick(
                    (btnData as any).team2.id,
                    true,
                    (btnData as any).team2.marketId,
                  )
                }
                priceChangeDirection={getPriceUpdate(1)?.direction}
                lastUpdateTimestamp={getPriceUpdate(1)?.timestamp}
                sx={{
                  width: { xs: '100%', sm: 120, md: 150 },
                  ...(compactThreeWaySx || {}),
                }}
              />
            </Box>
          )}
        </Box>

        {showLineRail && (
          <Box sx={{ mt: 2 }}>
            <LineSelector
              allLines={allLines}
              lineDisplayLabels={lineDisplayLabelsForRail}
              selectedLine={selectedLine || allLines[0]}
              onLineSelect={(line) => {
                setUserSelectedLine(line);

                if (onTradeClick) {
                  // Strategy: if the user has an active selection, try to maintain it on the new line.
                  // We find the index of the current active outcome in its market.
                  const currentOutcomes = getOutcomes(activeMarket);
                  let activeIndex = currentOutcomes.findIndex((o) => o.id === activeOutcomeId);

                  // If not found in activeMarket, the user might have selected from a
                  // different market (like Moneyline or 3-way) that isn't the active "line" market.
                  if (activeIndex === -1 && activeOutcomeId) {
                    (event.markets || []).some((m) => {
                      const mOutcomes = getOutcomes(m);
                      const idx = mOutcomes.findIndex((o) => o.id === activeOutcomeId);
                      if (idx !== -1) {
                        activeIndex = idx;
                        return true;
                      }
                      return false;
                    });
                  }

                  const targetIndex = activeIndex !== -1 ? activeIndex : 0;

                  // Find the new market for this line
                  const newMarket =
                    spreadRailSelectsByMarketId && lineGroupMarkets?.length
                      ? lineGroupMarkets.find((m: any) => String(m.id) === String(line))
                      : event.markets?.find((m: any) => {
                          const mType = (m.sportsMarketType || m.sportsType || '').toLowerCase();
                          const mLine = String(m.line || m.groupItemThreshold || '');
                          const targetLineStr = String(line);

                          const isCorrectType =
                            (isSpread && mType.includes('spread')) ||
                            (isTotal && mType.includes('total')) ||
                            mType === marketType.toLowerCase();

                          return isCorrectType && mLine === targetLineStr;
                        });

                  if (newMarket) {
                    const newOutcomes = getOutcomes(newMarket);
                    if (newOutcomes[targetIndex]) {
                      onTradeClick(
                        newMarket.id,
                        newOutcomes[targetIndex].id,
                        marketType,
                        true,
                        false,
                      );
                    }
                  }
                }

                if (onLineChange) {
                  onLineChange(
                    line,
                    marketType,
                    spreadRailSelectsByMarketId ? String(line) : undefined,
                  );
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default GameDetailCard;
